import { useRef, useCallback, useState } from "react";
import "./Blotter.styles.scss";

// Components Imports
import { Loading } from "@/layout";
import { BlotterHeader } from "./components/BlotterHeader";
import AlgoTable from "./components/AlgoTable/AlgoTable";
import type { BlotterAlgoTableRef } from "./components/AlgoTable/AlgoTable.types";

// Hooks Imports
import { useBlotterState, useAccountSelection, useDateRangePicker, useBlotterData } from "./hooks";

// Material UI Imports
import { SvgIcon, useTheme } from "@mui/material";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Constants Imports
import { POSITION_UPDATE, BALANCE_UPDATE } from "@/services/consts";

/**
 * Blotter Page - Refactored to follow AlgoManagement's maintainable/scalable structure.
 *
 * Architecture:
 * - Custom hooks manage state, data fetching, and business logic
 * - Custom AG Grid sidebar panel for all filtering/controls
 * - Main component orchestrates hooks and composes UI
 *
 * UI Structure:
 * - Header: Portfolio/Account selection (global filters)
 * - Sidebar Panel: Feed type selection, date filter, pagination controls
 * - Table: AG Grid with data
 *
 * Performance:
 * - Pagination token-based infinite scroll
 * - Debounced date changes (2500ms)
 * - Memoized callbacks and column definitions
 */
const Blotter = () => {
  // 1. Theme & Refs
  const theme = useTheme();
  const algoTableRef = useRef<BlotterAlgoTableRef>(null);
  const [allExpanded, setAllExpanded] = useState(false);

  // 2. Custom Hooks (orchestration layer)
  const { feedType, setFeedType } = useBlotterState();

  const {
    selectedAccount,
    selectedPortfolio,
    accountOptions,
    portfolioOptions,
    handlePortfolioChange,
    handleAccountChange,
    initializing,
  } = useAccountSelection();

  const { startDate, setStartDate, boundDates, historicalTimestamp } = useDateRangePicker(
    selectedAccount?.account_id || null,
    feedType
  );

  const { rowData, isLoading, hasMore, fetchMore } = useBlotterData({
    accountId: selectedAccount?.account_id,
    feedType,
    startDate,
  });

  // 3. Event Handlers
  const handleLoadMore = useCallback(async () => {
    const currentRowCount = rowData.length;
    await fetchMore();
    // After new data loads, scroll to first new row so AG Grid renders it (virtualization enabled)
    if (algoTableRef.current) {
      algoTableRef.current.ensureRowsVisible(currentRowCount);
    }
  }, [fetchMore, rowData]);

  const handleToggleExpandAll = useCallback(() => {
    algoTableRef.current?.toggleExpandAll();
    // Update state based on actual expanded rows
    const hasExpanded = algoTableRef.current?.hasExpandedRows() || false;
    setAllExpanded(hasExpanded);
  }, []);

  // Show loading during initialization
  if (initializing) {
    return (
      <div
        className="pulse-sidenav-adjusted-container"
        style={{ backgroundColor: theme.palette.bg["900"] }}
      >
        <Loading />
      </div>
    );
  }

  // 4. Render (composition only)
  return (
    <div
      className="pulse-sidenav-adjusted-container blotter-container"
      style={{ backgroundColor: theme.palette.bg["900"] }}
    >
      {/* Logo Background */}
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <LogoSVG style={{ color: theme.palette.bg[800] }} />
      </SvgIcon>

      {/* Header: Title + Portfolio/Account Dropdowns */}
      <BlotterHeader
        selectedPortfolio={selectedPortfolio}
        selectedAccount={selectedAccount}
        portfolioOptions={portfolioOptions}
        accountOptions={accountOptions}
        onPortfolioChange={handlePortfolioChange}
        onAccountChange={handleAccountChange}
      />

      {/* Table: AG Grid with custom sidebar panel for all controls */}
      <AlgoTable
        ref={algoTableRef}
        rowData={rowData}
        feedType={feedType}
        isLoading={isLoading}
        sidebarPanelProps={{
          feedType,
          onFeedTypeChange: setFeedType,
          startDate,
          onStartDateChange: setStartDate,
          boundDates,
          historicalTimestamp,
          hasMore,
          onLoadMore: handleLoadMore,
          onExpandAll: handleToggleExpandAll,
          allExpanded,
          showExpandAll: feedType === POSITION_UPDATE || feedType === BALANCE_UPDATE,
          hasAccount: !!selectedAccount,
        }}
      />
    </div>
  );
};

export default Blotter;